/* What a newly recorded thing does to an account's free cash.
 *
 * The book already worked this way in places and not in others, which is the
 * worst of both: selling credited the proceeds, a dividend credited its net,
 * opening a contract posted its margin — and buying shares, or paying money in,
 * left the balance exactly where it was. So the one figure you look at to
 * answer "what have I got spare" was right after some actions and stale after
 * others, with nothing on screen to say which.
 *
 * One rule, in one place, for all of them.
 *
 * ── what it does not do ───────────────────────────────────────────────────
 *
 * **A correction does not re-run what it records — it re-states it.** The rule
 * that a correction is not a transaction means it must not invent a second
 * event: no new trade, no second realised gain. It never meant the balance
 * should be left disagreeing with the record it came from.
 *
 * That distinction was got wrong first time round and the result was plainly
 * broken: recording a deposit raised the balance, deleting the same deposit
 * left the money there, and nothing on screen could put it back. So each record
 * remembers what it did (`CashEffect`), and correcting or deleting it undoes
 * exactly that and nothing else — at the amount that was actually applied, not
 * at today's rate. A record from before this existed remembers nothing, and so
 * deleting it moves nothing, which is also right.
 *
 * **A file is history, not something that just happened.** Importing two years
 * of deposits or a broker's whole trade log must not move today's balance:
 * those flows were spent long ago, and adding them up would put forty thousand
 * dollars into an account holding eleven. Bulk paths do not call this.
 *
 * **It never invents a rate.** A flow in a currency the account is not held in
 * converts only if a rate is known, and otherwise leaves the balance alone and
 * says so. A figure the user is the authority on is not the place to write a
 * guess. */

import type { AccountRecord, CashBalance, CashEffect, FxRates } from '../types';
import { toUsd } from './derive';
import { buyCost, saleProceeds } from './commission';

export interface CashMove {
  account: string;
  /** The new balance, not the delta — what `setCash` wants. */
  amount: number;
  currency: string;
  /** What was actually applied, in the account's currency, for the message. */
  applied: number;
  /** True when the amount had to be converted to the account's currency. */
  converted: boolean;
}

/** The account's currency, from its balance if it has one and its record if not. */
export function cashCurrency(
  balances: CashBalance[], accounts: AccountRecord[], account: string,
): string {
  const held = balances.find((c) => c.account === account);
  if (held) return held.currency;
  return accounts.find((a) => a.id === account)?.currency ?? 'USD';
}

/**
 * `amount` is signed and in `currency`: positive puts money into the account,
 * negative takes it out. Null means the balance must be left alone — no
 * account, or a currency pair with no rate to cross it.
 */
export function moveCash(
  balances: CashBalance[],
  accounts: AccountRecord[],
  account: string,
  amount: number,
  currency: string,
  fx: FxRates | undefined,
): CashMove | null {
  if (!account || !Number.isFinite(amount) || amount === 0) return null;

  const held = balances.find((c) => c.account === account);
  const target = cashCurrency(balances, accounts, account);
  const from = currency.toUpperCase();

  let applied = amount;
  let converted = false;
  if (from !== target.toUpperCase()) {
    const usd = toUsd(amount, from, fx);
    if (usd === null) return null;
    if (target.toUpperCase() === 'USD') applied = usd;
    else if (target.toUpperCase() === 'PLN' && fx?.usdPln) applied = usd * fx.usdPln;
    else return null;
    converted = true;
  }

  return {
    account,
    amount: (held?.amount ?? 0) + applied,
    currency: target,
    applied,
    converted,
  };
}

/** What a share trade does to cash: a buy takes the consideration and the
    commission out, a sale puts the consideration less the commission in.
    Nothing here nets against anything — each fill is its own movement, in the
    currency the listing trades in. */
export function tradeCashDelta(
  side: 'buy' | 'sell', shares: number, price: number, fee = 0,
): number {
  return side === 'buy'
    ? -buyCost(shares, price, fee)
    : saleProceeds(shares, price, fee);
}


/* Folding a set of movements onto the balances they land on.
 *
 * Deltas arrive already in the account's own currency, because that is how a
 * `CashEffect` is stored — so this is arithmetic and never a conversion. More
 * than one can land on the same account in one go: correcting a deposit is the
 * old effect reversed and the new one applied, and if both are the same account
 * they must be added before the balance is written or the second write would
 * discard the first. */
export function settle(
  balances: CashBalance[],
  deltas: Array<CashEffect | null | undefined>,
): CashMove[] {
  const byAccount = new Map<string, { currency: string; delta: number }>();
  for (const d of deltas) {
    if (!d || !d.account || !Number.isFinite(d.amount) || d.amount === 0) continue;
    const seen = byAccount.get(d.account);
    if (seen) seen.delta += d.amount;
    else byAccount.set(d.account, { currency: d.currency, delta: d.amount });
  }

  const out: CashMove[] = [];
  for (const [account, { currency, delta }] of byAccount) {
    if (delta === 0) continue;
    const held = balances.find((c) => c.account === account);
    out.push({
      account,
      amount: (held?.amount ?? 0) + delta,
      currency: held?.currency ?? currency,
      applied: delta,
      converted: false,
    });
  }
  return out;
}

/** The same movement, backwards — what undoing a record costs. */
export function reverse(effect: CashEffect | undefined): CashEffect | null {
  return effect ? { ...effect, amount: -effect.amount } : null;
}

/** The effect to store on a record, from a movement that was applied. */
export function effectOf(move: CashMove | null): CashEffect | undefined {
  return move ? { account: move.account, amount: move.applied, currency: move.currency } : undefined;
}


/* What a correction should do to the balance.
 *
 * `was` is what the record did when it was written, `now` is what it would do
 * if written today. The pair, ready to `settle`.
 *
 * The case that needs stating: a record from before any of this existed did
 * nothing to a balance. Correcting it must not apply the new effect either, or
 * fixing a typo on a two-year-old purchase is the moment the money leaves — a
 * withdrawal nobody made, triggered by an edit. Such a record stays outside
 * this arrangement until it is deleted and entered again. */
export function restating(
  was: CashEffect | undefined,
  now: CashEffect | undefined,
): Array<CashEffect | null> {
  if (!was) return [];
  return [reverse(was), now ?? null];
}
